import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { getContextType } from "@/lib/authorization";
import { instance } from "@/lib/axios/instance";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import { LoginRequest } from "@/types/request/LoginRequest";
import {
  LoginErrorResponse,
  LoginResponse,
  TokenPayload,
} from "@/types/response/LoginResponses";
import { UserRole } from "@/types/user/userType";

/**
 * Hook to handle user login mutation.
 * Handles JWT decoding, localStorage updates, and navigation after successful login.
 *
 * This hook is primarily used in auth-specific components (like LoginForm).
 * For general app components that need to check auth state, use useAuth() from the global context.
 *
 * @returns Mutation object for login operation
 *
 * @example
 * ```tsx
 * const loginMutation = useLoginMutation();
 * loginMutation.mutate({ email: "user@example.com", password: "password" });
 * ```
 */
export function useLoginMutation() {
  const navigate = useNavigate();
  const [, setUserId] = useLocalStorage("userId", "");
  const [, updateAccessToken] = useLocalStorage("accessToken", "");
  const [, setRole] = useLocalStorage<UserRole | null>("role", null);

  return useMutation<
    AxiosResponse<LoginResponse>,
    AxiosError<LoginErrorResponse>,
    LoginRequest
  >({
    mutationFn: (loginRequest: LoginRequest) =>
      instance.post<LoginResponse>("/api/v2/Auth/login", loginRequest),
    onSuccess: ({
      data: { accessToken, refreshToken },
    }: AxiosResponse<LoginResponse>) => {
      if (!accessToken || !refreshToken) {
        throw new Error("Invalid JWT token");
      }
      const decodedToken: TokenPayload = jwtDecode(accessToken);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp && decodedToken.exp < currentTime) {
        throw new Error("Token expired");
      }
      updateAccessToken(accessToken);
      setRole((decodedToken.role as UserRole) || null);
      setUserId(decodedToken.nameid || "");

      localStorage.setItem("email", decodedToken.email || "");
      localStorage.setItem("firstname", decodedToken.firstName || "");
      localStorage.setItem("infix", decodedToken.infix || "");
      localStorage.setItem("lastname", decodedToken.lastName || "");
      localStorage.setItem("refreshToken", refreshToken);
      const context = getContextType({
        clientId: decodedToken.clientId,
      });

      if (context === "admin") {
        navigate(`/${ROUTES.clients}`, { replace: true });
        return;
      }
      // context is "client"
      navigate(`/${ROUTES.home}`, { replace: true });
    },
  });
}
